import * as d3 from 'd3';

import { IAxisBase } from './IAxisBase';
import { HorizontalScale, IScale, VerticalScale } from '../scales/index';
import { NumberFormatHelper } from '../../../helpers/helpers';

export class AxisBase implements IAxisBase {
    private _axis: d3.svg.Axis;
    private _groupBase: d3.Selection<any>;
    private _isAxisVisible: boolean;
    private _isTitleVisible: boolean;
    private _scale: d3.scale.Linear<any, any>;
    private _size: number;
    private _ticksSize: number;
    private _titleText: string;
    private _wtScale: IScale;
    titleHeight = 20;
    titleMargin = 0;
    titleWidth = 50;

    constructor(orientation: string,
        size: number,
        min: number,
        max: number,
        ticksCount: number,
        container: d3.Selection<any>) {
        if (orientation === 'left' || orientation === 'right') {
            this._wtScale = new VerticalScale(size, min, max);
            this._groupBase = container
                .append('g')
                .classed('vertical-axis', true);
        } else if (orientation === 'top' || orientation === 'bottom') {
            this._wtScale = new HorizontalScale(size, min, max);
            this._groupBase = container
                .append('g')
                .classed('horizontal-axis', true);
        }
        this._groupBase.classed('axis', true);
        this.initTitle();

        this._ticksSize = 15;
        this._scale = this._wtScale.scale();
        this._axis = d3.svg.axis()
            .scale(this._scale)
            .ticks(ticksCount)
            .orient(orientation);
        this.update(min, max);
        this.isTitleVisible(false);
        this.isAxisVisible(true);
    }

    private initTitle() {
        const axisTitleGroup = this._groupBase.append('g')
            .classed('axis-title', true);
        axisTitleGroup.append('text')
            .style('text-anchor', 'middle');
    }

    size(value?: number): number {
        if (value) {
            this._size = value;
        }
        return this._size;
    }

    update(min: number, max: number): void {
        if (!this._wtScale.isUpdated(min, max) ||
            min !== this._wtScale.scale().range()[0] ||
            max !== this._wtScale.scale().range()[1]) {
            this._wtScale.update(min, max);
            this._scale = this._wtScale.scale();

            let fmt = this.tickFormat();
            if (!fmt) {
                fmt = NumberFormatHelper.getFormat(min, max);
            }
            this._axis.tickFormat(fmt);
            this.refreshAxis();
        }
    }

    translate(translateX: number, translateY: number): void {
        this._groupBase
            .attr({
                transform: `translate(${translateX ? translateX : 0},${translateY ? translateY : 0})`
            });
    }

    scale(): d3.scale.Linear<any, any> {
        return this._scale;
    }

    wtScale() {
        return this._wtScale;
    }

    resize(size) {
        this.size(size);
        this._wtScale.size(this.size());
        this._scale = this._wtScale.scale();
        this.refreshAxis();
    }

    tickFormat(value?) {
        if (value) {
            this._tickFormat = value;
            this._axis.tickFormat(value);
            this.refreshAxis();
        }
        return this._tickFormat;
    }

    tickvalues(value?) {
        if (value) {
            this._axis.tickValues(value);
            this.refreshAxis();
        }
    }

    tickCount(value?: number): number {
        if (value) {
            this._axis.ticks(value);
        }
        return this._axis.ticks().length;
    }

    isTitleVisible(value?: boolean): boolean {
        if (value !== null && value !== undefined) {
            this._isTitleVisible = value;
            const g = this._groupBase.selectAll('.axis-title');
            if (this._isTitleVisible) {
                g.style('visibility', 'visible');
            } else {
                g.style('visibility', 'hidden');
            }
        }
        return this._isTitleVisible;
    }

    isAxisVisible(value?: boolean): boolean {
        if (value !== null && value !== undefined) {
            this._isAxisVisible = value;
            this._groupBase.classed('hidden-group', !this._isAxisVisible);
        }
        return this._isAxisVisible;
    }

    titleText(value?: string): string {
        if (value) {
            this._titleText = value;
            this.titleWidth = (this._titleText.length + 1) * 10;
            const g = this.group().selectAll('g.axis-title');
            g.selectAll('text').text(this._titleText);
        }
        return this._titleText;
    }

    space(value?: number) {
        let size = 0;
        if (this.isAxisVisible()) {
            if (value) {
                this._ticksSize = value;
            }
            size = this._ticksSize;
            size = size + this.titleHeight + 2 * this.titleMargin;
        }
        return size;
    }

    group() {
        return this._groupBase;
    }

    apply() {
        this.refreshAxis();
    }

    tickSize(value?: number): number {
        if (arguments.length) {
            this._ticksSize = value;
        }
        return this._ticksSize;
    }

    
    private _ratio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            this._ratio = value;
            return this;
        }
        return this._ratio;
    }

    private refreshAxis() {
        this._axis.scale(this._scale);
        this._axis(this._groupBase);
        this._groupBase.selectAll('path')
            .classed('axis', true);
        const ticks = this._groupBase
            .selectAll('.tick');
        ticks.selectAll('line')
            .classed('tickLine', true);
        ticks.selectAll('text')
            .classed('tickLabel', true);
    }

    private _tickFormat: any;
}